<script setup lang="ts">
import { computed } from 'vue';
import { parseISO } from 'date-fns';
import { useToast } from 'vue-toastification';
import { Loader2 } from 'lucide-vue-next';
import CustomSelect from '@/components/ui/CustomSelect.vue';
import DatePicker from '@/components/ui/DatePicker.vue';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import Spinner from '@/components/ui/Spinner.vue';
import { bulkPurchasePayload, entryFor, evaluateBulkPurchaseRow, isBulkPurchaseEntryDirty, type BulkPurchaseRowProblem } from '@/composables/inventory/bulkPurchase';
import { useBulkEntryDialog } from '@/composables/inventory/useBulkEntryDialog';
import { drugUnitValue, formatQuantity } from '@/composables/inventory/inventoryTable';
import { i18n } from '@/plugins/internationalization/i18n';
import { useMedicineBulkPurchase, useSaveMedicineBulkPurchase } from '@/plugins/store/actions/queries/inventory';
import type { MedicineBulkPurchaseRow } from '@/types';
import UnsavedInputWarning from './UnsavedInputWarning.vue';

/*
 * The farm-wide purchase, the counterpart of the bulk count: every drug the farm has an instruction for in one
 * table, the bought ones ticked, each starting from the packs and pack size it was last bought in.
 */
const isOpen = defineModel<boolean>('open', { required: true });

const toast = useToast();
const t = (key: string, params: Record<string, unknown> = {}) => i18n.global.t(`views.health.inventory.bulkPurchase.${key}`, params);

// Every opening starts from a blank purchase. What was typed for a row survives a change of date, not of farm; a row
// left untouched starts again from the last purchase on or before the new date.
const {
    writableFarms,
    farmId,
    dateValue,
    dateOnly,
    todayIso,
    entries,
    serverErrors,
    generalErrors,
    rows,
    isFetching,
    isError,
    refetch,
    isSaving,
    submit,
    pendingAction,
    currentFarmName,
    requestFarm,
    requestOpen,
    confirm,
    keep,
} = useBulkEntryDialog({ isOpen, useRows: useMedicineBulkPurchase, useSave: useSaveMedicineBulkPurchase, entryFor, isEntryDirty: isBulkPurchaseEntryDirty });

const states = computed(() =>
    rows.value.flatMap((row) => {
        const entry = entries[drugUnitValue(row)];

        return entry ? [{ row, entry, state: evaluateBulkPurchaseRow(entry) }] : [];
    })
);
const boughtRows = computed(() => states.value.filter(({ state }) => !state.skipped));
const hasProblems = computed(() => boughtRows.value.some(({ state }) => state.problems.length > 0));
const canSave = computed(() => farmId.value !== null && dateOnly.value !== '' && boughtRows.value.length > 0 && !hasProblems.value && !isFetching.value && !isSaving.value);

function problemText(problem: BulkPurchaseRowProblem): string {
    return t(`problems.${problem}`);
}

/** Under the drug: when it was last bought, which is what its packs and pack size start from. */
function lastPurchaseText(row: MedicineBulkPurchaseRow): string {
    return row.last_purchase ? t('lastPurchase', { date: i18n.global.d(parseISO(row.last_purchase.date), 'short') }) : t('firstPurchase');
}

async function save() {
    if (!canSave.value || farmId.value === null) {
        return;
    }

    const saved = await submit(bulkPurchasePayload(farmId.value, dateOnly.value, rows.value, entries));

    if (saved === null) {
        toast.error(t('saveFailed'));
    } else {
        toast.success(t('saved', { count: saved }));
    }
}
</script>

<template>
    <!-- Closing goes through the guard too (the X, Cancel, Esc or a click outside); a save closes directly. -->
    <Dialog :model-value="isOpen" :disabled="isSaving" class="sm:max-w-[1100px]" @update:model-value="requestOpen">
        <DialogHeader>
            <DialogTitle>{{ t('title') }}</DialogTitle>
            <DialogDescription class="mt-2 text-gray-500">{{ t('description') }}</DialogDescription>
        </DialogHeader>

        <!-- Farm and date as on the bulk count. -->
        <div class="flex flex-wrap items-start gap-4">
            <div class="w-72">
                <!-- Picking another farm goes through the guard: with something typed, the farm stays until the switch is confirmed. -->
                <CustomSelect
                    :value="farmId"
                    :label="$t('views.health.inventory.table.headings.farm')"
                    :items="writableFarms"
                    item-text="name"
                    item-value="farm_id"
                    required
                    :allow-empty="false"
                    @update:value="requestFarm"
                />
            </div>
            <div class="w-48">
                <DatePicker v-model:value="dateValue" :resetable="false" required :max="todayIso" :label="$t('views.health.inventory.table.headings.date')" />
            </div>
        </div>

        <UnsavedInputWarning v-if="pendingAction?.kind === 'switchFarm'" action="switchFarm" :farm-name="currentFarmName" @confirm="confirm" @keep="keep" />

        <div v-if="isError" class="py-10 text-center text-gray-500 space-y-4">
            <div>{{ $t('views.pigs.records.errors.couldNotLoadData') }}</div>
            <Button variant="outline" @click="refetch()">{{ $t('views.pigs.records.errors.retry') }}</Button>
        </div>
        <div v-else-if="isFetching && rows.length === 0" class="py-10 flex justify-center">
            <Spinner class="size-6 animate-spin" />
        </div>
        <div v-else-if="rows.length === 0" class="py-10 text-center text-gray-500">{{ t('empty') }}</div>
        <div v-else class="max-h-[50vh] overflow-auto rounded-lg border border-gray-200">
            <table class="w-full text-normal">
                <thead class="sticky top-0 z-10 bg-gray-50 text-left text-gray-500">
                    <tr>
                        <th class="w-10 px-3 py-2" />
                        <th class="px-3 py-2 font-medium">{{ $t('views.health.inventory.table.headings.drug') }}</th>
                        <th class="w-28 px-3 py-2 font-medium">{{ t('headings.packs') }}</th>
                        <th class="w-40 px-3 py-2 font-medium">{{ t('headings.packSize') }}</th>
                        <th class="px-3 py-2 text-right font-medium">{{ t('headings.total') }}</th>
                        <th class="w-36 px-3 py-2 font-medium">{{ t('headings.batchNumber') }}</th>
                        <th class="px-3 py-2 font-medium">{{ t('headings.note') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <!-- Typing into a row ticks it: a figure entered for a drug means it was bought. -->
                    <tr v-for="{ row, entry, state } in states" :key="drugUnitValue(row)" class="border-t border-gray-200 align-top" :class="{ 'bg-gray-50 text-gray-400': state.skipped }">
                        <td class="px-3 py-3">
                            <Checkbox :checked="entry.bought" :aria-label="t('include', { drug: row.drug_name })" @update:checked="entry.bought = $event === true" />
                        </td>
                        <td class="px-3 py-2">
                            <div>
                                <span class="font-medium" :class="{ 'text-gray-900': !state.skipped }">{{ row.drug_name }}</span>
                                <span class="text-gray-500"> ({{ row.unit_name }})</span>
                            </div>
                            <div class="text-sm text-gray-500">{{ lastPurchaseText(row) }}</div>
                        </td>
                        <td class="px-3 py-2">
                            <Input
                                v-model="entry.packs"
                                type="number"
                                inputmode="numeric"
                                min="1"
                                step="1"
                                autocomplete="off"
                                class="h-9 bg-white"
                                :class="{ 'border-red-500': state.problems.includes('invalidPacks') }"
                                :aria-label="`${t('headings.packs')} ${row.drug_name}`"
                                @input="entry.bought = true"
                            />
                            <div v-if="state.problems.includes('invalidPacks')" class="pt-1 text-sm text-red-600">{{ problemText('invalidPacks') }}</div>
                        </td>
                        <td class="px-3 py-2">
                            <div class="flex items-center gap-1.5">
                                <Input
                                    v-model="entry.packSize"
                                    type="number"
                                    inputmode="decimal"
                                    min="0"
                                    step="any"
                                    autocomplete="off"
                                    class="h-9 bg-white"
                                    :class="{ 'border-red-500': state.problems.includes('invalidPackSize') }"
                                    :aria-label="`${t('headings.packSize')} ${row.drug_name}`"
                                    @input="entry.bought = true"
                                />
                                <span class="text-sm text-gray-500">{{ row.unit_name }}</span>
                            </div>
                            <div v-if="state.problems.includes('invalidPackSize')" class="pt-1 text-sm text-red-600">{{ problemText('invalidPackSize') }}</div>
                        </td>
                        <td class="px-3 py-2 text-right whitespace-nowrap">
                            <span v-if="state.total !== null" class="font-semibold text-gray-900">{{ formatQuantity(state.total) }} {{ row.unit_name }}</span>
                            <span v-else class="text-gray-400" aria-hidden="true">-</span>
                        </td>
                        <td class="px-3 py-2">
                            <Input
                                v-model="entry.batchNumber"
                                type="text"
                                maxlength="100"
                                autocomplete="off"
                                class="h-9 bg-white"
                                :aria-label="`${t('headings.batchNumber')} ${row.drug_name}`"
                                @input="entry.bought = true"
                            />
                        </td>
                        <td class="px-3 py-2">
                            <Input v-model="entry.note" type="text" maxlength="500" autocomplete="off" class="h-9 bg-white" :aria-label="`${t('headings.note')} ${row.drug_name}`" @input="entry.bought = true" />
                            <div v-if="serverErrors[drugUnitValue(row)]" class="pt-1 text-sm text-red-600">{{ serverErrors[drugUnitValue(row)] }}</div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="generalErrors.length" class="text-sm text-red-600" role="alert">
            <div v-for="message in generalErrors" :key="message">{{ message }}</div>
        </div>

        <UnsavedInputWarning v-if="pendingAction?.kind === 'close'" action="close" :farm-name="currentFarmName" @confirm="confirm" @keep="keep" />

        <DialogFooter class="pt-2">
            <DialogClose as-child>
                <Button variant="outline" class="min-w-32" :disabled="isSaving">{{ t('cancel') }}</Button>
            </DialogClose>
            <!-- While saving the button stays at full colour with a spinner; save() itself ignores further clicks. -->
            <Button class="min-w-44" :disabled="!canSave && !isSaving" :aria-busy="isSaving" @click="save">
                <Loader2 v-if="isSaving" class="size-4 animate-spin" aria-hidden="true" />
                {{ t('save', { count: boughtRows.length }) }}
            </Button>
        </DialogFooter>
    </Dialog>
</template>
